import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Badge — uses tonal (alpha-mixed) backgrounds rather than solid pills,
 * so several can sit next to each other without screaming.
 *
 * ⛔ color-mix 는 Chromium 111+ 다. 관제 보드 WebView 106 에서는 Tailwind 가 만든 대체값이 **순색 var(--x)** 라
 * 배경과 글자가 같은 색이 되어 「정상·심각」 글자가 안 보였다(2026-09-30 감사). 그래서 기본은 정적 `-soft`·`-fg`
 * 토큰이고, color-mix 를 아는 브라우저에서만(`supports-[…]:`) 예전 색 공식을 쓴다 — 최신 브라우저 모양은 그대로.
 */
const badge = cva(
  [
    "inline-flex items-center gap-1 px-2 py-0.5 rounded-[var(--radius-full)]",
    "text-[11px] font-medium tracking-[-0.005em] tabular-nums",
    // 한글은 음절 사이 어디서나 줄이 바뀐다 — 좁은 목록에서 「운영 일일」이 「운영 일 / 일」로
    // 갈라졌다(2026-09-15 지적). 배지는 낱말 단위로만 접고(「운영 / 일일」), 접히면 가운데 정렬.
    "break-keep text-center justify-center leading-[1.25]",
    "border border-transparent",
  ],
  {
    variants: {
      tone: {
        neutral:
          "bg-[var(--bg-subtle)] text-[var(--fg-muted)]",
        info:
          "bg-[var(--accent-soft)] supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] text-[var(--accent-text)]",
        success:
          "bg-[var(--success-soft)] text-[var(--success-fg)] supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[color-mix(in_oklab,var(--success)_18%,transparent)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-[color-mix(in_oklab,var(--success)_60%,#0d3d24)]",
        caution:
          "bg-[var(--caution-soft)] text-[var(--caution-fg)] supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[color-mix(in_oklab,var(--caution)_22%,transparent)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-[color-mix(in_oklab,var(--caution)_55%,#3d2d05)]",
        warning:
          "bg-[var(--warning-soft)] text-[var(--warning-fg)] supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[color-mix(in_oklab,var(--warning)_18%,transparent)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-[color-mix(in_oklab,var(--warning)_60%,#3d1f05)]",
        danger:
          "bg-[var(--danger-soft)] text-[var(--danger-fg)] supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[color-mix(in_oklab,var(--danger)_18%,transparent)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-[color-mix(in_oklab,var(--danger)_60%,#3d0a0a)]",
      },
      solid: {
        true: "border-transparent text-white",
        false: "",
      },
    },
    compoundVariants: [
      // 단색 배지는 tone 의 `supports-[color:color-mix(in_oklab,red_1%,red)]:` 색 공식까지 덮어야 한다 — 안 그러면 최신 브라우저에서 옅은 색이 이긴다.
      // 다크 — 옅은 배지 글자는 라이트용 어두운 혼합색(…_60%,#3d0a0a)이 어두운 바탕에 1.9~3.1:1 로 묻힌다.
      // 테마의 --*-fg(밝게 섞은 등급색)로 바꿔 4.5:1 이상을 지킨다. 단색 배지는 건드리지 않는다.
      { solid: false, tone: "success", class: "[:root[data-theme=dark]_&]:text-[var(--success-fg)]" },
      { solid: false, tone: "caution", class: "[:root[data-theme=dark]_&]:text-[var(--caution-fg)]" },
      { solid: false, tone: "warning", class: "[:root[data-theme=dark]_&]:text-[var(--warning-fg)]" },
      { solid: false, tone: "danger",  class: "[:root[data-theme=dark]_&]:text-[var(--danger-fg)]" },
      { solid: true, tone: "info",    class: "bg-[var(--accent-solid)] text-[var(--accent-fg)] supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[var(--accent-solid)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-[var(--accent-fg)]" },
      { solid: true, tone: "success", class: "bg-[var(--success)] text-black/85 supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[var(--success)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-black/85" },
      { solid: true, tone: "caution", class: "bg-[var(--caution)] text-black/85 supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[var(--caution)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-black/85" },
      { solid: true, tone: "warning", class: "bg-[var(--warning)] text-white supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[var(--warning)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-white" },
      { solid: true, tone: "danger",  class: "bg-[var(--danger)] text-white supports-[color:color-mix(in_oklab,red_1%,red)]:bg-[var(--danger)] supports-[color:color-mix(in_oklab,red_1%,red)]:text-white" },
    ],
    defaultVariants: { tone: "neutral", solid: false },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badge> {}

export function Badge({ className, tone, solid, ...p }: BadgeProps) {
  return <span className={cn(badge({ tone, solid }), className)} {...p} />;
}
